// All non-Innovaccer brands are served on this same domain under /<brand>/...
// The browser URL shows /<brand>/resources/<category>/<slug> while Next.js
// rewrites internally serve the matching local page (our CMS design).
export const BRAND_SLUGS = new Set(['gravity', 'flow', 'comet', 'galaxy', 'atlas', 'storyhealth']);

// Every brand a post/webinar can belong to, for pickers like the Replicate
// button's "which brand is this copy for?" prompt — the brand a post is
// tagged with drives which NavBar/Footer it renders (see each client-page.tsx)
// and which URL it lives at (brandExternalUrl above), so changing it here is
// sufficient to re-skin the whole page without touching anything else.
export const BRAND_OPTIONS: { value: string; label: string }[] = [
  { value: 'innovaccer', label: 'Innovaccer' },
  { value: 'gravity',    label: 'Gravity' },
  { value: 'flow',       label: 'Flow' },
  { value: 'galaxy',     label: 'Galaxy' },
  { value: 'comet',      label: 'Comet' },
  { value: 'atlas',      label: 'Atlas' },
  { value: 'storyhealth', label: 'Story Health' },
];

// Maps MDX category → the URL path segment
const CATEGORY_PATH: Record<string, string> = {
  'White Paper':       'resources/white-papers',
  'Case Study':        'resources/case-studies',
  'Industry Report':   'resources/industry-reports',
  'Brochure':          'resources/brochures',
  'Webinar':           'resources/webinars',
  'Newsroom':          'news',
  // Quoted aliases — guard against malformed frontmatter
  "'White Paper'":     'resources/white-papers',
  "'Case Study'":      'resources/case-studies',
  "'Industry Report'": 'resources/industry-reports',
  "'Brochure'":        'resources/brochures',
  // Blog editorial categories fall through to 'blogs' (see default below)
};

/**
 * Returns the brand-prefixed URL for a post, or null when the post
 * belongs to innovaccer (or has no brand) and should stay on the root site.
 *
 * Stories and Newsroom use short paths: /<brand>/blogs/<slug>, /<brand>/news/<slug>.
 * Other resource categories keep the /<brand>/resources/<category>/<slug> pattern.
 * These paths are served via Next.js rewrites so the CMS design is preserved.
 */
export function brandExternalUrl(
  brand: string | undefined,
  slug: string,
  category?: string,
): string | null {
  if (!brand || brand === 'innovaccer') return null;
  if (!BRAND_SLUGS.has(brand)) return null;
  // Story Health's Stories live under /storyhealth/resources/blogs, not the
  // short /blogs path every other brand uses.
  const defaultPath = brand === 'storyhealth' ? 'resources/blogs' : 'blogs';
  const categoryPath = (category && CATEGORY_PATH[category]) ?? defaultPath;
  return `/${brand}/${categoryPath}/${slug}`;
}

// Only Comet/Flow/Galaxy/Story Health have their own dedicated admin dashboard.
// Innovaccer and Gravity posts are managed from the main /admin, and Atlas has
// no admin of its own yet — both fall back there too.
const BRAND_ADMIN_BASE_PATH: Record<string, string> = {
  comet: '/comet/admin',
  flow: '/flow/admin',
  galaxy: '/galaxy/admin',
  storyhealth: '/storyhealth/admin',
};

/**
 * Returns the admin dashboard base path (e.g. "/flow/admin") a post's brand
 * belongs to, so "back to All posts" / breadcrumb links in the editor return
 * to the dashboard the post was opened from instead of always /admin.
 */
export function adminBasePathForBrand(brand: string | undefined): string {
  return (brand && BRAND_ADMIN_BASE_PATH[brand]) || '/admin';
}
